<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useContentHelper } from '~/composables/useContentHelper'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import SimpleCard from '~/components/cards/SimpleCard.vue'

const props = defineProps<{
  header: BaseItem
  showTitle?: boolean
}>()

const { findContents } = useContentApi()
const { packageTypes } = useContentHelper()

const { data, isLoading } = useQuery({
  staleTime: Infinity,
  queryKey: computed(() => ['teacher-header', props.header.contentId]),
  queryFn: async () => {
    const children = await findContents<BaseItem>({
      parentLocationIdCriterion: [props.header.mainLocationId],
      sortField: props.header.sortField,
      sortOrder: props.header.sortOrder,
    })
    return {
      headers: children
        .filter(({ contentTypeIdentifier }) => packageTypes.includes(contentTypeIdentifier)),
      cards: children
        .filter(({ contentTypeIdentifier }) => !packageTypes.includes(contentTypeIdentifier))
    }
  },
})
</script>

<template>
  <h3
    v-if="showTitle"
    v-text="props.header.title"
  />

  <RichTextRenderer
    v-if="props.header.intro"
    :text="props.header.intro"
  />

  <AuSkeletonWrapper
    v-if="isLoading"
    class="grid gap-px border border-transparent sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
  >
    <AuSkeleton
      v-for="i in 12"
      :key="`skeleton-${i}`"
      height="80px"
      background="var(--color-orange-5)"
      class="!rounded-none border border-transparent"
    />
  </AuSkeletonWrapper>

  <template v-else-if="data">
    <ul class="not-prose grid grid-cols-[repeat(auto-fill,minmax(172px,1fr))] gap-px">
      <li
        v-for="card in data.cards"
        :key="`card-${card.locationId}`"
        class="flex"
      >
        <SimpleCard
          :resource="card"
          no-image
        />
      </li>
    </ul>

    <TeacherSubitems
      v-for="subheader in data.headers"
      :key="`header-${subheader.locationId}`"
      :header="subheader"
      :show-title="true"
    />
  </template>
</template>
